/** Small, dependency-free geometry helpers for the map screens. Everything here is a convenience for DRAWING (a friendly warning before the
 *  server says no, a live size while the outline is being drawn); the server's PostGIS decides validity and computes the real area. */
export type Pos = [lon: number, lat: number]
export type GeoJsonGeometry = { type: string; coordinates: unknown[]; [extra: string]: unknown }

const M_PER_DEG_LAT = 111_320

const same = (a: Pos, b: Pos) => a[0] === b[0] && a[1] === b[1]

/** Orientation of the triple (a, b, c): > 0 anticlockwise, < 0 clockwise, 0 collinear (planar maths: fine at the scale of a golf course). */
const orient = (a: Pos, b: Pos, c: Pos) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
const onSegment = (a: Pos, b: Pos, p: Pos) => Math.min(a[0], b[0]) <= p[0] && p[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= p[1] && p[1] <= Math.max(a[1], b[1])

/** Do the segments ab and cd touch or cross? */
export function segmentsIntersect(a: Pos, b: Pos, c: Pos, d: Pos): boolean {
  const o1 = orient(a, b, c), o2 = orient(a, b, d), o3 = orient(c, d, a), o4 = orient(c, d, b)
  if (((o1 > 0 && o2 < 0) || (o1 < 0 && o2 > 0)) && ((o3 > 0 && o4 < 0) || (o3 < 0 && o4 > 0))) return true
  return (o1 === 0 && onSegment(a, b, c)) || (o2 === 0 && onSegment(a, b, d)) || (o3 === 0 && onSegment(c, d, a)) || (o4 === 0 && onSegment(c, d, b))
}

/** An OPEN ring (first vertex not repeated) of at least three distinct vertices whose edges do not cross or touch except at the shared corners. */
export function isSimpleRing(ring: Pos[]): boolean {
  const n = ring.length
  if (n < 3) return false
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) if (same(ring[i]!, ring[j]!)) return false
  if (Math.abs(ringArea(ring)) === 0) return false
  for (let i = 0; i < n; i++) {
    const a = ring[i]!, b = ring[(i + 1) % n]!
    for (let j = i + 1; j < n; j++) {
      const c = ring[j]!, d = ring[(j + 1) % n]!
      const adjacent = j === i + 1 || (i === 0 && j === n - 1)
      if (adjacent) {
        // neighbours share one corner; they must not also overlap along the line (a "spike" back over itself)
        const shared = j === i + 1 ? b : a
        const other1 = same(shared, a) ? b : a, other2 = same(shared, c) ? d : c
        if (orient(other1, shared, other2) === 0 && onSegment(shared, other1, other2) || orient(other1, shared, other2) === 0 && onSegment(shared, other2, other1)) return false
        continue
      }
      if (segmentsIntersect(a, b, c, d)) return false
    }
  }
  return true
}

/** Signed planar area in square degrees of an open ring (only its sign and zero-ness are used). */
function ringArea(ring: Pos[]): number {
  let s = 0
  for (let i = 0; i < ring.length; i++) { const a = ring[i]!, b = ring[(i + 1) % ring.length]!; s += a[0] * b[1] - b[0] * a[1] }
  return s / 2
}

/** Approximate area in m2 of an open ring, on a local flat projection around its own latitude. A live preview only. */
export function approxAreaM2(ring: Pos[]): number {
  if (ring.length < 3) return 0
  const lat0 = ring.reduce((s, p) => s + p[1], 0) / ring.length
  const kx = M_PER_DEG_LAT * Math.cos((lat0 * Math.PI) / 180)
  let s = 0
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!, b = ring[(i + 1) % ring.length]!
    s += (a[0] * kx) * (b[1] * M_PER_DEG_LAT) - (b[0] * kx) * (a[1] * M_PER_DEG_LAT)
  }
  return Math.abs(s) / 2
}

/** Approximate length in metres of a polyline. A live preview only. */
export function approxLengthM(line: Pos[]): number {
  let t = 0
  for (let i = 1; i < line.length; i++) {
    const a = line[i - 1]!, b = line[i]!
    const kx = M_PER_DEG_LAT * Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180)
    t += Math.hypot((b[0] - a[0]) * kx, (b[1] - a[1]) * M_PER_DEG_LAT)
  }
  return t
}

export const closeRing = (ring: Pos[]): Pos[] => (ring.length > 0 && !same(ring[0]!, ring[ring.length - 1]!) ? [...ring, ring[0]!] : ring)

/** A GeoJSON Polygon from an open ring of vertices (first vertex repeated at the end, as GeoJSON wants). */
export const polygonFromRing = (ring: Pos[]): GeoJsonGeometry => ({ type: 'Polygon', coordinates: [closeRing(ring)] })

/** The outer ring of a Polygon (or the first polygon of a MultiPolygon) as an OPEN ring; empty for anything else. */
export function outerRing(geom: GeoJsonGeometry | null | undefined): Pos[] {
  if (!geom) return []
  const c = geom.coordinates as unknown
  const ring = geom.type === 'Polygon' ? (c as Pos[][])[0] : geom.type === 'MultiPolygon' ? (c as Pos[][][])[0]?.[0] : undefined
  if (!ring || ring.length < 4) return []
  return ring.slice(0, -1).map((p) => [p[0], p[1]] as Pos)
}

/** The outer ring (open) when the shape is ONE polygon with no holes: the only kind the on-map editor can edit without losing something. */
export function editableRing(geom: GeoJsonGeometry | null | undefined): Pos[] | null {
  if (!geom) return null
  const c = geom.coordinates as unknown
  const poly = geom.type === 'Polygon' ? (c as Pos[][]) : geom.type === 'MultiPolygon' && (c as Pos[][][]).length === 1 ? (c as Pos[][][])[0] : undefined
  if (!poly || poly.length !== 1) return null
  return outerRing({ type: 'Polygon', coordinates: poly })
}

/** Every position of a geometry, flattened (for a bounding box). */
export function positionsOf(geom: GeoJsonGeometry): Pos[] {
  const out: Pos[] = []
  const walk = (c: unknown) => {
    if (Array.isArray(c) && typeof c[0] === 'number' && typeof c[1] === 'number') out.push([c[0], c[1]])
    else if (Array.isArray(c)) c.forEach(walk)
  }
  walk(geom.coordinates)
  return out
}

export type Bounds = [west: number, south: number, east: number, north: number]
export function boundsOf(geoms: Array<GeoJsonGeometry | null | undefined>): Bounds | null {
  let b: Bounds | null = null
  for (const g of geoms) {
    if (!g) continue
    for (const [x, y] of positionsOf(g)) b = b ? [Math.min(b[0], x), Math.min(b[1], y), Math.max(b[2], x), Math.max(b[3], y)] : [x, y, x, y]
  }
  return b
}

/** "1 024 m²" under a hectare, "1,23 ha" above: what a greenkeeper reads. */
export function formatArea(m2: number, locale: string): string {
  const fmt = (n: number, d: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: d, minimumFractionDigits: d }).format(n)
  return m2 >= 10_000 ? `${fmt(m2 / 10_000, 2)} ha` : `${fmt(Math.round(m2), 0)} m²`
}
export function formatLength(m: number, locale: string): string {
  const fmt = (n: number, d: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: d, minimumFractionDigits: d }).format(n)
  return m >= 1000 ? `${fmt(m / 1000, 2)} km` : `${fmt(Math.round(m), 0)} m`
}
